'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { BaseGameSettings, DEFAULT_GAME_SETTINGS, UseGameSettingsReturn } from '@/contexts/games/types';

/**
 * Универсальный хук для управления настройками игры
 * Автоматически сохраняет и загружает настройки из localStorage
 */
export function useGameSettings<T extends BaseGameSettings = BaseGameSettings>(
  gameId: string,
  defaultSettings?: Partial<T>
): UseGameSettingsReturn<T> {
  const [settings, setSettings] = useState<T>(() => {
    // Объединяем базовые настройки с переданными по умолчанию
    return {
      ...DEFAULT_GAME_SETTINGS,
      ...defaultSettings,
    } as T;
  });
  
  const [isLoading, setIsLoading] = useState(true);

  const storageKey = `owlcat-game-settings-${gameId}`;

  // Загрузка настроек из localStorage
  const loadSettings = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      
      if (typeof window === 'undefined') {
        setIsLoading(false);
        return;
      }

      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        
        // Объединяем сохраненные настройки с текущими (на случай добавления новых настроек)
        const mergedSettings = {
          ...DEFAULT_GAME_SETTINGS,
          ...defaultSettings,
          ...parsedSettings,
        } as T;
        
        setSettings(mergedSettings);
      }
    } catch (error) {
      console.error('Failed to load game settings:', error);
      // В случае ошибки используем настройки по умолчанию
      setSettings({
        ...DEFAULT_GAME_SETTINGS,
        ...defaultSettings,
      } as T);
    } finally {
      setIsLoading(false);
    }
  }, [gameId, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Сохранение настроек в localStorage
  const saveSettings = useCallback(async (settingsToSave?: T): Promise<void> => {
    try {
      if (typeof window === 'undefined') return;
      
      const key = `owlcat-game-settings-${gameId}`;
      const dataToSave = settingsToSave || settings;
      localStorage.setItem(key, JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Failed to save game settings:', error);
      throw error;
    }
  }, [gameId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Обновление настроек
  const updateSettings = useCallback((updates: Partial<T>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...updates };
      
      // Автоматически сохраняем, если включено автосохранение
      if (newSettings.autoSave) {
        // Используем setTimeout чтобы не блокировать UI
        setTimeout(() => {
          try {
            if (typeof window !== 'undefined') {
              const key = `owlcat-game-settings-${gameId}`;
              localStorage.setItem(key, JSON.stringify(newSettings));
            }
          } catch (error) {
            console.error('Failed to auto-save game settings:', error);
          }
        }, 0);
      }
      
      return newSettings;
    });
  }, [gameId]); // используем gameId напрямую, а не storageKey

  // Сброс настроек к значениям по умолчанию
  const resetSettings = useCallback(() => {
    const defaultSettingsValue = {
      ...DEFAULT_GAME_SETTINGS,
      ...defaultSettings,
    } as T;
    
    setSettings(defaultSettingsValue);
    
    // Автоматически сохраняем сброшенные настройки
    if (defaultSettingsValue.autoSave) {
      setTimeout(() => {
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem(storageKey, JSON.stringify(defaultSettingsValue));
          }
        } catch (error) {
          console.error('Failed to save reset settings:', error);
        }
      }, 0);
    }
  }, [storageKey, defaultSettings]);

  // Загружаем настройки ОДИН раз при монтировании. Guarded by a ref: React dev
  // StrictMode re-runs mount effects, and a second load would overwrite a change
  // the page applied in between (e.g. MazeGame's `?mode=image` from the URL —
  // the persisted write is deferred via setTimeout, so the re-load still saw the
  // OLD stored mode and reverted the UI to it).
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadSettings();
  }, [loadSettings]);

  // Обработка изменений в localStorage (синхронизация между вкладками)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue) {
        try {
          const newSettings = JSON.parse(event.newValue);
          setSettings(prev => ({ ...prev, ...newSettings }));
        } catch (error) {
          console.error('Failed to sync settings from storage:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [storageKey]);

  return {
    settings,
    isLoading,
    updateSettings,
    resetSettings,
    saveSettings,
    loadSettings,
  };
}
